import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Field, getIn } from 'formik'
import classNames from 'classnames'
import { FormGroup } from '@peracto/peracto-ui'
import { useConfig } from '@peracto/peracto-config'

const getCharacterCount = html => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent.length || 0
}

const RichTextEditor = ({ field, form, name, label, help, className, themeColours, CKEditor, ClassicEditor, ...props }) => {
    useEffect(() => {
        const domEditableElement = document.querySelector('.ck-editor__editable')
        const editorInstance = domEditableElement?.ckeditorInstance

        if (editorInstance) {
            editorInstance.editing.view.change(writer => {
                const viewEditableRoot =
                    editorInstance.editing.view.domConverter.mapDomToView(
                        domEditableElement
                    )
                writer.setAttribute('data-testid', `richtext-${name}`, viewEditableRoot)
            })
        }
    }, [name])

    useEffect(() => {
        const temp = document.getElementsByClassName('ck-editor__main')
        for (let index = 0; index < temp.length; index++) {
            const t1 = document.getElementsByClassName('ck-editor__main')[index]
            const t2 = document.createAttribute('data-testid')
            t2.value = `richtext-attribute-${index}`
            t1.setAttributeNode(t2)
        }
    })

    let error = getIn(form.errors, name)

    if (error) {
        const errorFieldName = label ? label : 'This'
        error = error.replace(field.name, errorFieldName)
    }

    props.className = classNames('form-control', className)

    if (help) {
        props['aria-describedby'] = `${name}Help`
    }

    return (
        <FormGroup
            name={name}
            label={label}
            help={help}
            charactercount={getCharacterCount(field.value || '')}
            error={error}
        >
            <CKEditor
                editor={ClassicEditor}
                config={{
                    placeholder: props.placeholder
                        ? props.placeholder
                        : 'Enter your text here...',
                    toolbar: {
                        items: [
                            'heading',
                            '|',
                            'bold',
                            'italic',
                            'link',
                            'bulletedList',
                            'numberedList',
                            '|',
                            'alignment',
                            '|',
                            'indent',
                            'outdent',
                            '|',
                            'fontFamily',
                            'fontColor',
                            '|',
                            'blockQuote',
                            'horizontalLine',
                            'insertTable',
                            'mediaEmbed',
                            'ckfinder',
                            '|',
                            'undo',
                            'redo',
                            'removeFormat',
                        ],
                    },
                    fontColor: {
                        colors: [
                            ...themeColours,
                            // Include CKEditor defaults
                            { color: 'hsl(0, 0%, 0%)', label: 'Black' },
                            { color: 'hsl(0, 0%, 30%)', label: 'Dim grey' },
                            { color: 'hsl(0, 0%, 60%)', label: 'Grey' },
                            { color: 'hsl(0, 0%, 90%)', label: 'Light grey' },
                            {
                                color: 'hsl(0, 0%, 100%)',
                                label: 'White',
                                hasBorder: true,
                            },
                            { color: 'hsl(0, 75%, 60%)', label: 'Red' },
                            { color: 'hsl(30, 75%, 60%)', label: 'Orange' },
                            { color: 'hsl(60, 75%, 60%)', label: 'Yellow' },
                            { color: 'hsl(90, 75%, 60%)', label: 'Light green' },
                            { color: 'hsl(120, 75%, 60%)', label: 'Green' },
                            { color: 'hsl(150, 75%, 60%)', label: 'Aquamarine' },
                            { color: 'hsl(180, 75%, 60%)', label: 'Turquoise' },
                            { color: 'hsl(210, 75%, 60%)', label: 'Light blue' },
                            { color: 'hsl(240, 75%, 60%)', label: 'Blue' },
                            { color: 'hsl(270, 75%, 60%)', label: 'Purple' },
                        ],
                    },
                }}
                data={field.value || ''}
                onChange={(event, editor) => {
                    form.setFieldValue(name, editor.getData())
                }}
                disabled={form.isSubmitting || props.disabled}
            />
        </FormGroup>
    )
}

const RichText = ({ name, label, help, className, showWhen, ...props }) => {
    const editorRef = useRef()
    const [editorLoaded, setEditorLoaded] = useState(false)
    const config = useConfig()
    const { colorOptions = [] } = config.get('editorConfig') || {}

    const { CKEditor, ClassicEditor } = editorRef.current || {}

    // Map colorOptions to CKEditor format
    const themeColours = colorOptions.map(colour => ({
        label: colour.label,
        color: colour.value,
    }))

    useEffect(() => {
        const { ClassicEditor } = require('@peracto/ckeditor')
        const { CKEditor: CKEditorReact } = require('@ckeditor/ckeditor5-react')

        editorRef.current = {
            CKEditor: CKEditorReact,
            ClassicEditor: ClassicEditor,
        }
        setEditorLoaded(true)
    }, [])

    return (
        editorLoaded && (
            <Field name={name}>
                {({ field, form }) => {
                    if (showWhen) {
                        const showField = form.values.hasOwnProperty(showWhen)
                            ? form.values[showWhen]
                            : false
                        if (!showField) {
                            return null
                        }
                    }

                    return (
                        <RichTextEditor
                            field={field}
                            form={form}
                            name={name}
                            label={label}
                            help={help}
                            className={className}
                            themeColours={themeColours}
                            CKEditor={CKEditor}
                            ClassicEditor={ClassicEditor}
                            {...props}
                        />
                    )
                }}
            </Field>
        )
    )
}

RichText.displayName = 'RichText'

RichText.propTypes = {
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    help: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    placeholder: PropTypes.string,
    className: PropTypes.string,
    showWhen: PropTypes.string,
}

RichText.defaultProps = {
    type: 'text',
}

export default RichText
